/**
 * services/sync/SoftDeleteUtils.ts
 * Utility functions for soft delete operations on synced tables
 *
 * Provides a standalone soft delete helper for tables that don't have
 * dedicated service classes. This avoids the need to create service
 * classes just for deletion operations.
 *
 * For tables WITH services, use BaseService.softDelete() which adds
 * hook support (beforeDelete/afterDelete).
 */

import { db } from '@/lib/db';
import { getOrCreateDeviceId } from '@/utils/deviceDetection';
import { syncCoordinatorService } from './SyncCoordinatorService';
import { SYNC_TABLES, type SyncTableName } from '@/constants/syncTables';

/**
 * Options for softDeleteRecord
 */
export interface SoftDeleteOptions {
  /**
   * Whether to notify other tabs of the deletion via SyncCoordinatorService.
   * Defaults to true.
   *
   * Set to false when calling from BaseService.softDelete() to allow
   * the service to control notification timing (after afterDelete hook).
   */
  notify?: boolean;
}

/**
 * Result of softDeleteRecord
 */
export interface SoftDeleteResult {
  /**
   * True if the record was actually deleted (tombstoned).
   * False if the record was already tombstoned (no-op).
   */
  deleted: boolean;
}

/**
 * Soft delete a record from a synced table.
 *
 * Sets tombstone fields (deletedAt, deletedByDevice, updatedAt) and
 * optionally notifies other tabs via SyncCoordinatorService.
 *
 * This function is idempotent: calling it on an already-tombstoned
 * record is a no-op that returns { deleted: false }.
 *
 * @param tableName - Name of the sync table (must be in SYNC_TABLES)
 * @param id - ID of the record to delete
 * @param options - Optional configuration (notify defaults to true)
 * @returns Result indicating whether deletion occurred
 * @throws Error if tableName is not in SYNC_TABLES
 * @throws Error if record with given ID does not exist
 *
 * @example
 * // Basic usage (notifies other tabs)
 * await softDeleteRecord('wizard_drafts', 'onboarding-draft-id');
 *
 * @example
 * // Called from BaseService (controls notify timing)
 * const result = await softDeleteRecord('instruments', id, { notify: false });
 * if (result.deleted) {
 *   await this.afterDelete?.(id);
 *   syncCoordinatorService.notifyChange('instruments', id);
 * }
 */
export async function softDeleteRecord(
  tableName: SyncTableName,
  id: string,
  options?: SoftDeleteOptions
): Promise<SoftDeleteResult> {
  // Runtime guard: validate tableName is in SYNC_TABLES
  // This catches typos and prevents operations on non-synced tables
  if (!SYNC_TABLES.includes(tableName)) {
    throw new Error(
      `[softDeleteRecord] Invalid table name: "${tableName}". Must be one of SYNC_TABLES.`
    );
  }

  // Get table reference using dynamic access
  // Guard against schema drift or partial initialization
  const table = db[tableName];
  if (!table) {
    throw new Error(
      `[softDeleteRecord] Table "${tableName}" not found in database. SYNC_TABLES may be out of sync with schema.`
    );
  }

  // Type assertion for table methods
  const typedTable = table as {
    get: (id: string) => Promise<Record<string, unknown> | undefined>;
    update: (id: string, changes: Record<string, unknown>) => Promise<number>;
  };

  // Fetch the record
  const record = await typedTable.get(id);
  if (!record) {
    throw new Error(
      `[softDeleteRecord] Record not found: ${tableName}/${id}`
    );
  }

  // Idempotent check: if already tombstoned, return early
  // Inline check per CODING_STANDARDS (types directory should be type-only)
  const deletedAt = record['deletedAt'];
  if (typeof deletedAt === 'string' && deletedAt.length > 0) {
    return { deleted: false };
  }

  // Set tombstone fields
  const now = new Date().toISOString();
  const deviceId = getOrCreateDeviceId();

  const tombstoneData = {
    deletedAt: now,
    deletedByDevice: deviceId,
    updatedAt: now,
  };

  // Update the record
  const updateCount = await typedTable.update(id, tombstoneData);

  // Race condition guard: verify update succeeded
  // This handles the case where the record was deleted between get and update
  if (updateCount === 0) {
    throw new Error(
      `[softDeleteRecord] Race condition: record ${tableName}/${id} was modified or deleted during soft delete`
    );
  }

  // Notify other tabs if enabled (default: true)
  if (options?.notify !== false) {
    syncCoordinatorService.notifyChange(tableName, id);
  }

  return { deleted: true };
}
